import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClipboardCheck, Activity, Printer, Plus, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { openPrintWindow } from '@/lib/letterhead';
import { LIVELLO_STILE, LivelloBadge, AlertRow } from './alertStyles';
import RevisioneFarmaciCard from './RevisioneFarmaciCard';
import CaduteCard from './CaduteCard';
import LesioniCard from './LesioniCard';
import ContenzioniCard from './ContenzioniCard';
import ConfirmDialog from './ConfirmDialog';

export default function ScaleTab({ ospiteId, ospite }) {
  const { hasRole } = useAuth();
  const [dati, setDati] = useState(null);
  const [defs, setDefs] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [compila, setCompila] = useState(null);
  const [risposte, setRisposte] = useState({});
  const [note, setNote] = useState('');
  const [storico, setStorico] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);
  const puoCompilare = hasRole('coordinatore', 'medico', 'infermiere');

  const fetchAll = useCallback(() => {
    api.get(`/scale/ospite/${ospiteId}`).then(r => setDati(r.data)).catch(() => {});
    api.get(`/alert/ospite/${ospiteId}`).then(r => setAlerts(r.data)).catch(() => {});
  }, [ospiteId]);

  useEffect(() => {
    fetchAll();
    api.get('/scale/definizioni').then(r => setDefs(r.data.scale)).catch(() => {});
  }, [fetchAll]);

  const apriCompila = (tipo) => { setCompila(tipo); setRisposte({}); setNote(''); };

  const salva = async () => {
    const def = defs[compila];
    const mancante = def.items.find(i => risposte[i.id] === undefined);
    if (mancante) return toast.error(`Rispondi a: ${mancante.testo}`);
    try {
      const r = await api.post(`/scale/ospite/${ospiteId}`, { tipo: compila, risposte, note });
      toast.success(`${r.data.scala_nome}: ${r.data.punteggio}/${r.data.punteggio_max} - ${r.data.classe}`);
      setCompila(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const elimina = async () => {
    try {
      await api.delete(`/scale/voce/${daEliminare.id}`);
      toast.success('Valutazione eliminata');
      setDaEliminare(null); setStorico(null); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const stampa = (v) => {
    const righe = Object.values(v.risposte).map(r =>
      `<tr><td>${r.testo}</td><td style="text-align:right">${r.risposta}</td><td style="text-align:right">${r.punti}</td></tr>`).join('');
    openPrintWindow(v.scala_nome, `
      <h2>${v.scala_nome}</h2>
      <p><b>Ospite:</b> ${ospite?.cognome || ''} ${ospite?.nome || ''} &nbsp;&nbsp; <b>Data valutazione:</b> ${v.data_valutazione}</p>
      <table style="width:100%;border-collapse:collapse" border="1" cellpadding="6">
        <thead><tr><th align="left">Voce</th><th>Risposta</th><th>Punti</th></tr></thead>
        <tbody>${righe}</tbody>
        <tfoot><tr><th align="left" colspan="2">Punteggio totale</th><th>${v.punteggio}/${v.punteggio_max}</th></tr></tfoot>
      </table>
      <p><b>Esito:</b> ${v.classe}</p>
      <p><b>Indicazioni:</b> ${v.indicazione}</p>
      ${v.note ? `<p><b>Note:</b> ${v.note}</p>` : ''}
      <p style="margin-top:24px"><b>Rilevato da:</b> ${v.operatore_nome} &nbsp;&nbsp; <b>Prossima rivalutazione:</b> ${v.prossima_rivalutazione}</p>
    `);
  };

  if (!dati || !defs) return <div className="py-10 text-center text-sm text-slate-400">Caricamento scale...</div>;

  const news2 = alerts?.news2;

  return (
    <div className="space-y-4" data-testid="scale-tab">
      <Card className={alerts && alerts.livello !== 'verde' ? LIVELLO_STILE[alerts.livello].card : ''}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Activity className="w-4 h-4 text-brand" />Sorveglianza clinica
            {alerts && <LivelloBadge livello={alerts.livello} testid="alert-livello-ospite" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap" data-testid="news2-box">
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">NEWS2</span>
            {news2?.calcolabile ? (
              <>
                <span className={`text-2xl font-bold ${LIVELLO_STILE[news2.livello].text}`} data-testid="news2-punteggio">{news2.punteggio}</span>
                <LivelloBadge livello={news2.livello} testo={news2.indicazione.split(':')[0]} />
                {news2.parziale && <Badge variant="outline" className="text-[10px]">parziale - mancano: {news2.mancanti.join(', ')}</Badge>}
              </>
            ) : (
              <span className="text-xs text-slate-500">Non calcolabile: nessun parametro vitale nelle ultime 36 ore</span>
            )}
          </div>
          {news2?.calcolabile && (
            <div className="flex flex-wrap gap-2">
              {news2.dettaglio.map((d, i) => (
                <Badge key={i} variant="outline" className={`text-[10px] ${d.punti > 0 ? 'border-amber-300 text-amber-700' : ''}`}>
                  {d.voce}: {d.valore} ({d.punti} pt)
                </Badge>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {(alerts?.alert || []).map((a, i) => <AlertRow key={i} a={a} testid={`alert-ospite-${a.codice}`} />)}
            {alerts && alerts.alert.length === 0 && (
              <p className="text-xs text-emerald-700">Nessun alert clinico attivo per questo ospite.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(dati.scale).map(([tipo, s]) => {          const u = s.ultima;
          const stile = LIVELLO_STILE[u?.livello] || LIVELLO_STILE.verde;
          return (
            <Card key={tipo} data-testid={`scala-card-${tipo}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-800 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-brand" />{s.nome}</span>
                  {s.stato.mancante ? <Badge variant="outline" className="text-[10px]">Mai compilata</Badge>
                    : s.stato.scaduta ? <Badge className="text-[10px] bg-orange-100 text-orange-700">Da rivalutare</Badge>
                      : <Badge variant="outline" className="text-[10px]">Valida {s.stato.giorni_alla_scadenza} gg</Badge>}
                </CardTitle>
                <p className="text-[11px] text-slate-400">{s.ambito} · rivalutazione ogni {s.periodicita_giorni} giorni</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {u ? (
                  <div className="flex items-center gap-3">
                    <span className={`text-3xl font-bold ${stile.text}`} data-testid={`scala-punteggio-${tipo}`}>{u.punteggio}</span>
                    <span className="text-xs text-slate-400">/ {u.punteggio_max}</span>
                    <div className="flex-1">
                      <LivelloBadge livello={u.livello} testo={u.classe} testid={`scala-classe-${tipo}`} />
                      <p className="text-[11px] text-slate-400 mt-1">del {new Date(u.data_valutazione).toLocaleDateString('it-IT')} · {u.operatore_nome}</p>
                    </div>
                  </div>
                ) : <p className="text-xs text-slate-500">Nessuna valutazione registrata.</p>}
                {u?.indicazione && <p className="text-xs text-slate-600 italic">{u.indicazione}</p>}
                <div className="flex gap-2 flex-wrap">
                  {puoCompilare && (
                    <Button size="sm" className="h-8 text-xs gap-1" onClick={() => apriCompila(tipo)} data-testid={`compila-scala-${tipo}`}>
                      <Plus className="w-3 h-3" />Compila
                    </Button>
                  )}
                  {u && <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => stampa(u)} data-testid={`stampa-scala-${tipo}`}><Printer className="w-3 h-3" />Stampa</Button>}
                  {s.storico.length > 0 && (
                    <Button size="sm" variant="ghost" className="h-8 text-xs gap-1" onClick={() => setStorico({ tipo, nome: s.nome, voci: s.storico })} data-testid={`storico-scala-${tipo}`}>
                      <History className="w-3 h-3" />Storico ({s.storico.length})
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <RevisioneFarmaciCard ospiteId={ospiteId} />

      <CaduteCard ospiteId={ospiteId} onSaved={fetchAll} />

      <LesioniCard ospiteId={ospiteId} onSaved={fetchAll} />

      <ContenzioniCard ospiteId={ospiteId} onSaved={fetchAll} />

      <Dialog open={!!compila} onOpenChange={(o) => !o && setCompila(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{compila && defs[compila].nome}</DialogTitle></DialogHeader>
          {compila && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">{defs[compila].note_uso}</p>
              {defs[compila].items.map(item => (
                <div key={item.id} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center">
                  <span className="text-sm text-slate-700">{item.testo}</span>
                  <Select value={risposte[item.id] !== undefined ? String(risposte[item.id]) : ''}
                    onValueChange={(v) => setRisposte({ ...risposte, [item.id]: parseInt(v, 10) })}>
                    <SelectTrigger className="h-9 text-xs" data-testid={`item-${item.id}`}><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      {item.opzioni.map(o => <SelectItem key={o.punti} value={String(o.punti)}>{o.label} ({o.punti} pt)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              <Textarea placeholder="Note (facoltative)" value={note} onChange={e => setNote(e.target.value)} className="text-sm" data-testid="scala-note" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setCompila(null)}>Annulla</Button>
                <Button size="sm" onClick={salva} data-testid="salva-scala">Salva valutazione</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!storico} onOpenChange={(o) => !o && setStorico(null)}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Storico {storico?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(storico?.voci || []).map(v => (
              <div key={v.id} className="flex items-center justify-between border rounded-lg p-2.5">
                <div>
                  <p className="text-sm font-medium text-slate-800">{v.punteggio}/{v.punteggio_max} · {v.classe}</p>
                  <p className="text-[11px] text-slate-400">{new Date(v.data_valutazione).toLocaleDateString('it-IT')} · {v.operatore_nome}</p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => stampa(v)}><Printer className="w-3.5 h-3.5" /></Button>
                  {hasRole('coordinatore', 'medico') && (
                    <Button size="sm" variant="ghost" className="h-8 text-red-600" onClick={() => setDaEliminare(v)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!daEliminare} onClose={() => setDaEliminare(null)}
        title="Eliminare la valutazione?" message="L'operazione non e reversibile." onConfirm={elimina} />
    </div>
  );
}
